import { useMemo } from 'react';
import type { VictoryPieProps } from 'victory';
import { VictoryPie } from 'victory';

export interface PieChartSlice {
  name: string;
  color: string;
  percent: number;
}

export type PieChartSlices = PieChartSlice[];
export interface PieChartProps {
  slices?: PieChartSlices;
  hoveredIndex?: number | null;
  onSliceHover?: (index: number | null) => void;
}

export function PieChart({
  slices = [],
  hoveredIndex,
  onSliceHover,
}: PieChartProps = {}) {
  const data = slices.map((slice) => ({
    x: 0,
    y: slice.percent,
  }));

  const colorScale = slices.map((slice) => slice.color);

  const events: VictoryPieProps['events'] = useMemo(
    () =>
      onSliceHover
        ? [
            {
              target: 'data',
              eventHandlers: {
                onMouseEnter: () => [
                  {
                    target: 'data',
                    mutation: (mutationProps?: { index: number }): null => {
                      onSliceHover(mutationProps ? mutationProps.index : null);

                      return null;
                    },
                  },
                ],
                onMouseLeave: () => [
                  {
                    target: 'data',
                    mutation: (): null => {
                      onSliceHover(null);

                      return null;
                    },
                  },
                ],
              },
            },
          ]
        : undefined,
    [onSliceHover],
  );

  const style: VictoryPieProps['style'] = useMemo(
    () => ({
      data: {
        opacity: ({ index }) => {
          if (hoveredIndex === null) {
            return 1;
          }

          return index === hoveredIndex ? 1 : 0.3;
        },
        transition: 'opacity 300ms ease-out',
        cursor: onSliceHover ? 'pointer' : 'default',
      },
    }),
    [hoveredIndex, onSliceHover],
  );

  return (
    <div role="graphics-document">
      <VictoryPie
        data={data}
        colorScale={colorScale}
        events={events}
        style={style}
        padding={0}
        innerRadius={32}
        padAngle={1}
        radius={50}
        width={100}
        height={100}
        labels={[]}
        startAngle={-60}
        endAngle={300}
      />
    </div>
  );
}

PieChart.displayName = 'PieChart';
